import { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Breadcrumb, BreadcrumbItem } from '../primitives/Breadcrumb'
import { ActionBar } from '../components/ActionBar'
import { ActionButton } from '../components/ActionButton'
import { CommonPageLayout } from './CommonPageLayout'

interface PageWrapperProps {
  children: ReactNode
  className?: string
}

interface PageWrapperHeaderProps {
  /**
   * Breadcrumb items for navigation
   */
  breadcrumbItems: BreadcrumbItem[]
  /**
   * Icon to display in breadcrumb
   */
  breadcrumbIcon?: ReactNode
  /**
   * Primary actions displayed on the right
   */
  actions?: ReactNode
  /**
   * Action buttons configuration
   */
  actionButtons?: {
    label: string
    icon?: ReactNode
    onClick: () => void
    variant?: 'primary' | 'secondary' | 'danger'
    disabled?: boolean
  }[]
  /**
   * Title and info section (for detail pages)
   */
  titleSection?: {
    title: string
    badge?: ReactNode
    subtitle?: string
    metadata?: ReactNode
  }
  className?: string
}

/**
 * Root wrapper for all pages - provides background color and padding
 */
function PageWrapperRoot({ children, className }: PageWrapperProps) {
  return <CommonPageLayout className={className}>{children}</CommonPageLayout>
}

/**
 * Header section with breadcrumb and optional action bar
 */
function PageWrapperHeader({
  breadcrumbItems,
  breadcrumbIcon,
  actions,
  actionButtons,
  titleSection,
  className,
}: PageWrapperHeaderProps) {
  return (
    <div className={cn('mb-2 flex-shrink-0 space-y-4', className)}>
      <Breadcrumb icon={breadcrumbIcon} items={breadcrumbItems} />

      {/* Combined title section with action buttons */}
      {(titleSection || actions || actionButtons) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {/* Title and metadata section */}
          {titleSection && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h1 className="font-geist text-2xl font-semibold leading-8 text-[#262626]">
                  {titleSection.title}
                </h1>
                {titleSection.badge}
              </div>
              <div className="flex flex-col gap-0.5 font-geist text-sm font-normal leading-5 text-[#737373]">
                {titleSection.subtitle && <span>{titleSection.subtitle}</span>}
                {titleSection.metadata}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {(actions || actionButtons) && (
            <ActionBar>
              {actionButtons
                ? actionButtons.map((button, index) => (
                    <ActionButton
                      disabled={button.disabled}
                      icon={button.icon}
                      key={index}
                      onClick={button.onClick}
                      variant={button.variant}
                    >
                      {button.label}
                    </ActionButton>
                  ))
                : actions}
            </ActionBar>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Page wrapper component - Parent layout for all pages
 * Provides consistent background, padding, breadcrumb and actions
 *
 * @example
 * ```tsx
 * <PageWrapper>
 *   <PageWrapper.Header
 *     breadcrumbItems={[...]}
 *     actionButtons={[...]}
 *   />
 *   <DetailPageLayout.Content>
 *     <DetailPageLayout.Sidebar>...</DetailPageLayout.Sidebar>
 *     <DetailPageLayout.Main>...</DetailPageLayout.Main>
 *   </DetailPageLayout.Content>
 * </PageWrapper>
 * ```
 */
export const PageWrapper = Object.assign(PageWrapperRoot, {
  Header: PageWrapperHeader,
})
